import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Card, Skeleton } from '@/ui/primitives'
import { NewMenu } from '@/ui/new-menu'
import { lei } from '@/lib/money'

type Eveniment = { id: number; number: string; partner: string | null; customer?: string; destination: string | null }
type Scadenta = { id: number; number: string; partner: string | null; restant_ron: number; currency: string }
type Zi = {
  date: string; day_name: string; day_number: number; is_today: boolean; is_weekend: boolean
  plecari: Eveniment[]; sosiri: Eveniment[]
  scadente_client: Scadenta[]; scadente_furnizor: Scadenta[]
  total_events: number
}

/** Saptamana curenta, o coloana pe zi: plecari, sosiri si scadente — ce se intampla efectiv. */
export function Calendar() {
  const nav = useNavigate()
  const { data, isPending } = useQuery({
    queryKey: ['calendar-saptamana'], queryFn: () => api.get<{ days: Zi[] }>('/dashboard/weekly-calendar'),
  })

  return (
    <>
      <PageHeader title="Calendar" count="urmatoarele 7 zile" actions={<NewMenu />} />
      <div className="px-8 py-6">
        {isPending ? <Card><Skeleton rows={6} /></Card> : (
          <div className="grid gap-3 md:grid-cols-4 xl:grid-cols-7">
            {(data?.days ?? []).map((z) => (
              <Card key={z.date}
                className={'p-3 min-h-[190px] ' + (z.is_today ? 'border-accent' : z.is_weekend ? 'bg-page/60' : '')}>
                <div className="flex items-baseline justify-between mb-2.5">
                  <span className={'text-[13px] ' + (z.is_today ? 'text-accent-ink font-medium' : 'text-muted')}>
                    {z.day_name}
                  </span>
                  <span className="tnum text-[17px]">{z.day_number}</span>
                </div>
                <div className="space-y-2">
                  {z.plecari.map((p) => (
                    <button key={`p${p.id}`} onClick={() => nav(`/rezervari/${p.id}`)}
                      className="w-full text-left rounded-md px-2 py-1.5 bg-accent-soft hover:brightness-95 transition-all">
                      <span className="block text-[11.5px] text-accent-ink">plecare</span>
                      <span className="block text-[12.5px] truncate">{p.customer || p.partner}</span>
                      <span className="block text-[11.5px] text-muted truncate">{p.destination || ''}</span>
                    </button>
                  ))}
                  {z.sosiri.map((s) => (
                    <button key={`s${s.id}`} onClick={() => nav(`/rezervari/${s.id}`)}
                      className="w-full text-left rounded-md px-2 py-1.5 bg-good-soft hover:brightness-95 transition-all">
                      <span className="block text-[11.5px] text-good">sosire</span>
                      <span className="block text-[12.5px] truncate">{s.customer || s.partner}</span>
                    </button>
                  ))}
                  {z.scadente_client.map((s) => (
                    <button key={`sc${s.id}`} onClick={() => nav(`/bani/facturi/${s.id}`)}
                      className="w-full text-left rounded-md px-2 py-1.5 bg-warn-soft hover:brightness-95 transition-all">
                      <span className="block text-[11.5px] text-warn">de incasat</span>
                      <span className="block text-[12.5px] truncate">{s.partner}</span>
                      <span className="block text-[11.5px] tnum text-muted">{lei(s.restant_ron)} lei</span>
                    </button>
                  ))}
                  {z.scadente_furnizor.map((s) => (
                    <button key={`sf${s.id}`} onClick={() => nav(`/bani/facturi/${s.id}`)}
                      className="w-full text-left rounded-md px-2 py-1.5 bg-page hover:brightness-95 transition-all">
                      <span className="block text-[11.5px] text-muted">de platit</span>
                      <span className="block text-[12.5px] truncate">{s.partner}</span>
                      <span className="block text-[11.5px] tnum text-muted">{lei(s.restant_ron)} lei</span>
                    </button>
                  ))}
                  {z.total_events === 0 && <p className="text-[12px] text-muted">—</p>}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
